import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import App from './App';
import { YodaThemeProvider } from './styles/YodaThemeProvider';
import { StoreHydrationBoundary } from './components/StoreHydrationBoundary';
import { UploadProvider } from './shared/contexts/UploadContext';
import { queryClient } from './shared/config/queryClient.config';
import './shared/utils/storeCleanupUtils'; // Auto-imports cleanup manager
import './i18n';
import './index.css';
import 'driver.js/dist/driver.css';
import './styles/tourStyles.css';
import './styles/tourSelectionModal.css';

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  enableLogs: true,
  // Performance Monitoring
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  // Only enable in production
});

// 🆕 Phase 0.3: Create localStorage persister for TanStack Query with error handling
const CACHE_KEY = 'botunify-query-cache';

// Function to safely create persister with error recovery
const createSafePersister = () => {
  try {
    // Test if localStorage is accessible and not corrupted
    const testKey = `${CACHE_KEY}-test`;
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);

    // Try to read existing cache to check if it's valid
    const existingCache = window.localStorage.getItem(CACHE_KEY);
    if (existingCache) {
      try {
        JSON.parse(existingCache);
      } catch {
        window.localStorage.removeItem(CACHE_KEY);
      }
    }

    return createSyncStoragePersister({
      storage: window.localStorage,
      key: CACHE_KEY,
      // Add serialize/deserialize with error handling
      serialize: data => {
        try {
          return JSON.stringify(data);
        } catch (error) {
          console.error('Failed to serialize cache:', error);
          return '';
        }
      },
      deserialize: data => {
        try {
          return JSON.parse(data);
        } catch (error) {
          console.error('Failed to deserialize cache, clearing...', error);
          // Clear corrupted cache
          window.localStorage.removeItem(CACHE_KEY);
          return undefined;
        }
      },
    });
  } catch (error) {
    console.error('Failed to create persister:', error);
    // Return a no-op persister if localStorage is unavailable
    return {
      persistClient: async () => {},
      restoreClient: async () => undefined,
      removeClient: async () => {},
    };
  }
};

const persister = createSafePersister();

// Wrap rendering in try-catch to handle potential hydration errors
try {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <StoreHydrationBoundary>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{ persister }}
          onSuccess={() => {
            // Hydration complete - cache restored from localStorage
          }}
        >
          <UploadProvider>
            <YodaThemeProvider>
              <App />
            </YodaThemeProvider>
          </UploadProvider>
        </PersistQueryClientProvider>
      </StoreHydrationBoundary>
    </React.StrictMode>
  );
} catch (error) {
  console.error('Failed to render app due to corrupted cache:', error);
  // Clear all storage and try again
  window.localStorage.clear();
  window.location.reload();
}
